<template>
  <div class="wrap" :class="isMain ? 'main': 'sub'">
    <div v-if="loadingStatus.full" class="full-loading">
      <HconLoading v-model="loadingStatus.full" />
    </div>
    <HconTopBanner v-if="isMain && isVisibleTopBanner && topBannerList && topBannerList.length > 0" :banner-list="topBannerList"/>
    <HconComPCHeader :has-border="headerBorder" :isLoggedIn="loginFlag"></HconComPCHeader>
    <main class="container" :class="isMain === false ? 'inner' : ''">
      <HconCornerBreadcrumbs v-if="!isMain && !isDetail" :breadcrumbs="breadcrumbs" />
      <slot />
    </main>      
    <HconComPCFooter />
    <HconCornerAsideMenuPC />
  </div>
</template>
<script setup lang="ts">
import { usePopup  } from "@/composables/hconUsePopup";
import HconTopBanner, { type BannerItem } from '@/components/corner/pc/HconCornerMainTopBannerPC.vue'; // top banner
import HconComPCHeader from "@/components/com/HconComPCHeader.vue"; // header
import HconCornerBreadcrumbs from '@/components/corner/pc/HconCornerBreadcrumbsPC.vue'; // breadcrumb
import HconComPCFooter from "@/components/com/HconComPCFooter.vue"; // footer
import HconCornerAsideMenuPC from "@/components/corner/pc/HconCornerAsideMenuPC.vue"; // right floating
import HconLoading from '@/components/com/HconComPCLoading.vue'; // loading
import { computed, onMounted } from "vue";
import SessionStorageSynchronizer from '~/utils/sync-sessionstorage';

// 메인 페이지인 경우만 true
const route = useRoute();
const isMain = computed(() => route.meta.isMain === true)
const isDetail = computed(() => route.meta.isDetail === true)
const loginFlag = ref(true);
// 메인 페이지인 경우에는 header에 border 없음
const headerBorder = computed(() => route.meta.isMain !== true )

useHead({
  link: [
    {
      rel: 'icon',
      type: 'image/png',
      href: '/favicon.png'
    }
  ]
})

// 팝업
const {
  showAlert, isLoggedIn
} = usePopup();
const { recentSearchwordsInfo } = useHeader()
const fetchBuilder = useHconFetchBuilder();

// 로딩바 타입 정의
export interface Loadingstatus {
  full: boolean
}

// loading-state라는 키값으로 전역 상태 생성(기본값 false)
const loadingStatus = useState<Loadingstatus>('loading-status', () => ({
  full: false //전역 레이아웃 로딩
}))

// 라우트가 변경되면 무조건 로딩바를 끔
watch(() => route.fullPath, () => {
  loadingStatus.value.full = false
})

watch(isLoggedIn, async (newV, oldV) => {
  if(newV === true) {
    loginFlag.value = false;
    await nextTick();
    loginFlag.value = true;
  }

})

// 메인 페이지 상단 탑 배너 (다건, 최대 2개)
const isVisibleTopBanner = ref(true)
const topBannerList = ref<BannerItem[]>();



// 브래드 크럼 메뉴
const breadcrumbs = computed(() => {

  interface BreadcrumbItem {
    label : string        // 화면에 보여질 브래드크럼명 ex) 홈 > 마이 Hcon
    to: string            // 브래드크럼명 클릭 시 이동되는 경로(useRoute를 이용하여 자동으로 파일 폴더 경로대로 지정됨) ex) pc/mypage
    child?: any[]         // 하위 자식들 ex) pc/mypage의 경우 해당 경로 하위에 있는 값 들(구매관리, 핀발행 요청 관리, H캐시 관리 등) 
    category?: boolean    // 추후 상품목록에서 카테고리, 브랜드 별로 필터링 시 사용될 값
    hideChild?: boolean   // 하위 자식들 숨김 여부 ex) true => pc/mypage 자식들이 드롭다운으로 안보여짐
    skipParent?: string[] // 중간에 skip할 부모 브래드크럼들 => ex) ['마이 Hcon', '회원 정보 관리'] => 회원탈퇴 완료 페이지에서 해당 부모 브래드크럼을 skip
    movable?: boolean     // 브래드크럼명 클릭 시 이동 여부 => ex) 기획전 목록, 기획전 상세 페이지의 경우 브래드크럼 클릭 시 해당 경로로 이동 x
  }

  // 홈 경로
  const crumbs: BreadcrumbItem[] = [{label: '홈', to:'/pc/product/main'}]

  const skipParents = route.meta.skipParent as string[] || []

  // 라우팅 경로
  route.matched.forEach((record) => {

    const isLabel = record.meta.label as string
    const isChild = record.children as any[]
    const isCategory = record.meta.category as boolean
    const isHideChild = record.meta.hideChild as boolean
    const isMovable = record.meta.movable as boolean

    if(isLabel && !skipParents.includes(isLabel)) {
      
      // 상세페이지의 경우
      if(route.path.includes('detail') && isMovable) {
        const parentPath = route.path.split('/detail')[0]
        crumbs.push({
          label: isLabel,
          to: `${parentPath}/list`,
          child: isChild == undefined ? [] : isChild,               // default = []
          category: isCategory == undefined ? false : isCategory,   // default = false
          hideChild: isHideChild == undefined ? true : isHideChild, // default = true
          movable: isMovable == undefined ? true : isMovable        // default = true
        })
      } else if(route.path.includes('register') && isMovable) { // 등록페이지의 경우
        const parentPath = route.path.split('/register')[0]
        crumbs.push({
          label: isLabel,
          to: `${parentPath}/list`,
          child: isChild == undefined ? [] : isChild,               // default = []
          category: isCategory == undefined ? false : isCategory,   // default = false
          hideChild: isHideChild == undefined ? true : isHideChild, // default = true
          movable: isMovable == undefined ? true : isMovable        // default = true
        })
      }
      else {
        const isCurrentPath = route.path.endsWith('/list')
        crumbs.push({
          label: isLabel == undefined ? '' : isLabel,               // default = ''
          to: isCurrentPath ? '' : record.path as string,
          child: isChild == undefined ? [] : isChild,               // default = []
          category: isCategory == undefined ? false : isCategory,   // default = false
          hideChild: isHideChild == undefined ? true : isHideChild, // default = true
          movable: isMovable == undefined ? true : isMovable        // default = true
        })
      }

    } 

  })
  return crumbs
})

// 띠배너 조회 (1002)
const getTopBnnrList = async () => {
  const res = await fetchBuilder
    .get<AjaxResult<any>>('/api/v1/product/display/banner-list/1002')
    .progressMessage("잠시만 기다려주세요...")
    .build()
    .execute()
    .then((result) => {
      // 응답코드가 200이 아닌경우
      if(result.resultCode != '200') {
        showAlert(result.resultMessage)
      }
      else {
        topBannerList.value = result.data.map((bnnr:any) => {
          return {
            text: bnnr.bnnrPhrsCntn,
            link: bnnr.bnnrShctUrl,
            nwinMvYn: bnnr.nwinMvYn,
            bnnrSeq: bnnr.bnnrSeq
          }
        })
      }
    }) 
}

onMounted(() => {
  window.addEventListener('storage', (e) => {
    if (e.key !== 'auth:event' || !e.newValue) return;

    const event = JSON.parse(e.newValue);

    if (event.type === 'LOGOUT') {
      sessionStorage.removeItem('common.auth.info');
      SessionStorageSynchronizer.removeItem('common.auth.rememberMe');
      isLoggedIn.value = false;
      useHconAuthInfo().clear();
      const { $stopTimer } = useNuxtApp();
      $stopTimer();
      recentSearchwordsInfo.value = [];
      localStorage.setItem('auth:event', JSON.stringify({
          type: 'LOGOUT',
          at: Date.now()
      }));
    }
  })
  // 화면 최상단 띠배너 조회
  getTopBnnrList();


  // 비밀번호만료여부(만료 => 'PASS')
  const isExpiredPswd = useHconAuthInfo().getIfPossible()?.userContext.isExpiredPswd
    // 비밀번호만료일이 지난 경우 비밀번호변경캠페인 페이지로 이동
  if(isLoggedIn.value === true && isExpiredPswd === 'PASS') {
    navigateTo({path: '/pc/common/password'});
  }
});
</script>

<style lang="scss">
@use "@/assets/scss/pc/import-hcon.scss" as *;
@use "@/assets/scss/pc/pc-hcon.scss" as *;
</style>